import type { DocumentChatSidebarTranslations } from "@/lib/i18n/types/documents-sections/chat";

type ExampleCopy = DocumentChatSidebarTranslations["examples"];

/**
 * The empty state offers exactly three questions, and they should reflect what
 * has actually been happening on the platform rather than a fixed list.
 *
 * Two layers do that. A candidate declares the one condition under which its
 * question makes sense — "there are overdue items", "this building has a second
 * IFC revision" — and a FAMILY groups the candidates that compete for the same
 * slot. Scoring is per family, so the three questions on screen always come from
 * three different subjects; picking within a family rotates on a seed, so two
 * chats opened on identical data don't read identically.
 *
 * Everything here is pure and deterministic (no `Math.random`, no `Date.now()`),
 * matching how the rest of the chat varies its copy — see the status-pill
 * fallback in `SidebarChatMessages` and `getAvatarPalette` in `ui/user-avatar`.
 */
export type SuggestionFamily = "documents" | "issues" | "model" | "data" | "output" | "guide";

/**
 * Aggregate signals the questions are chosen from. `null` means "we asked and
 * could not find out" (a failed count), which is NOT the same as zero — a
 * candidate that would quote a number stays ineligible rather than guessing.
 */
export interface ChatSuggestionContext {
  documentCount: number | null;
  /** Documents added to the scope within `RECENT_WINDOW_DAYS`. */
  recentUploadCount: number | null;
  openIssueCount: number | null;
  overdueIssueCount: number | null;
  /** Issues opened within `RECENT_WINDOW_DAYS`. */
  newIssueCount: number | null;
  /** Open issues assigned to the signed-in user. */
  myOpenIssueCount: number | null;
  /** Processed IFC files in the scope. */
  ifcModelCount: number | null;
  /** Newest IFC that has a previous revision to compare against, if any. */
  ifcRevisionFilename: string | null;
  /** The PM module is available to this user at all. */
  canUseIssues: boolean;
  canViewBuildingData: boolean;
}

/** How far back "this week" reaches for uploads and newly opened issues. */
export const RECENT_WINDOW_DAYS = 7;

export const EMPTY_SUGGESTION_CONTEXT: ChatSuggestionContext = {
  documentCount: null,
  recentUploadCount: null,
  openIssueCount: null,
  overdueIssueCount: null,
  newIssueCount: null,
  myOpenIssueCount: null,
  ifcModelCount: null,
  ifcRevisionFilename: null,
  canUseIssues: false,
  canViewBuildingData: false,
};

interface SuggestionCandidate {
  id: keyof ExampleCopy;
  family: SuggestionFamily;
  isEligible: (ctx: ChatSuggestionContext) => boolean;
  /** The number this question quotes, when it quotes one. */
  count?: (ctx: ChatSuggestionContext) => number | null;
}

/** `count` is a real number greater than `min` — a failed count never qualifies. */
function above(value: number | null, min: number): boolean {
  return value !== null && value > min;
}

const CANDIDATES: SuggestionCandidate[] = [
  // --- documents -------------------------------------------------------------
  {
    id: "docsRecent",
    family: "documents",
    isEligible: (ctx) => above(ctx.recentUploadCount, 1),
    count: (ctx) => ctx.recentUploadCount,
  },
  {
    id: "docsRecentOne",
    family: "documents",
    isEligible: (ctx) => ctx.recentUploadCount === 1,
  },
  {
    id: "docsLatest",
    family: "documents",
    isEligible: (ctx) => above(ctx.recentUploadCount, 0),
  },
  {
    id: "docsBrief",
    family: "documents",
    isEligible: (ctx) => above(ctx.documentCount, 0),
  },
  {
    id: "docsVerified",
    family: "documents",
    isEligible: (ctx) => above(ctx.documentCount, 0),
  },
  {
    id: "docsEmpty",
    family: "documents",
    isEligible: (ctx) => ctx.documentCount === 0,
  },
  {
    id: "docsUnavailable",
    family: "documents",
    isEligible: (ctx) => ctx.documentCount === null,
  },

  // --- issues ----------------------------------------------------------------
  {
    id: "issuesOverdue",
    family: "issues",
    isEligible: (ctx) => above(ctx.overdueIssueCount, 1),
    count: (ctx) => ctx.overdueIssueCount,
  },
  {
    id: "issuesOverdueOne",
    family: "issues",
    isEligible: (ctx) => ctx.overdueIssueCount === 1,
  },
  {
    id: "issuesOpen",
    family: "issues",
    isEligible: (ctx) => above(ctx.openIssueCount, 1),
    count: (ctx) => ctx.openIssueCount,
  },
  {
    id: "issuesOpenOne",
    family: "issues",
    isEligible: (ctx) => ctx.openIssueCount === 1,
  },
  {
    id: "issuesNew",
    family: "issues",
    isEligible: (ctx) => above(ctx.newIssueCount, 1),
    count: (ctx) => ctx.newIssueCount,
  },
  {
    id: "issuesNewOne",
    family: "issues",
    isEligible: (ctx) => ctx.newIssueCount === 1,
  },
  {
    id: "issuesMine",
    family: "issues",
    isEligible: (ctx) => above(ctx.myOpenIssueCount, 1),
    count: (ctx) => ctx.myOpenIssueCount,
  },
  {
    id: "issuesMineOne",
    family: "issues",
    isEligible: (ctx) => ctx.myOpenIssueCount === 1,
  },
  {
    // Franklin can open the issue himself, so only offer it to someone who may.
    id: "issuesSuggest",
    family: "issues",
    isEligible: (ctx) => ctx.canUseIssues && above(ctx.documentCount, 0),
  },
  {
    id: "issuesEmpty",
    family: "issues",
    isEligible: (ctx) => ctx.openIssueCount === 0 || ctx.openIssueCount === null,
  },

  // --- model -----------------------------------------------------------------
  {
    id: "modelDiff",
    family: "model",
    isEligible: (ctx) => ctx.ifcRevisionFilename !== null,
  },
  {
    id: "modelDiffQuantities",
    family: "model",
    isEligible: (ctx) => ctx.ifcRevisionFilename !== null,
  },
  {
    id: "modelOverview",
    family: "model",
    isEligible: (ctx) => above(ctx.ifcModelCount, 0),
  },
  {
    id: "modelTakeoff",
    family: "model",
    isEligible: (ctx) => above(ctx.ifcModelCount, 0),
  },

  // --- building data ---------------------------------------------------------
  {
    id: "dataMissing",
    family: "data",
    isEligible: (ctx) => ctx.canViewBuildingData,
  },
  {
    id: "dataChanges",
    family: "data",
    isEligible: (ctx) => ctx.canViewBuildingData,
  },

  // --- generated output ------------------------------------------------------
  {
    id: "outputPdf",
    family: "output",
    isEligible: (ctx) => above(ctx.documentCount, 0),
  },
  {
    id: "outputExcel",
    family: "output",
    isEligible: (ctx) => above(ctx.openIssueCount, 0),
  },
  {
    id: "outputDocx",
    family: "output",
    isEligible: (ctx) => above(ctx.documentCount, 0),
  },

  // --- guide -----------------------------------------------------------------
  // Always eligible, always last: with `documents` and `issues` also carrying an
  // always-true fallback, three families exist even on an empty building.
  { id: "guideCapabilities", family: "guide", isEligible: () => true },
  { id: "guideUpload", family: "guide", isEligible: () => true },
];

/**
 * Tie-break order, so two families on the same score always resolve the same
 * way. Also the order the chosen three are rendered in.
 */
const FAMILY_ORDER: SuggestionFamily[] = [
  "issues",
  "documents",
  "model",
  "data",
  "output",
  "guide",
];

/**
 * How loudly each family is asking to be one of the three. 0 = stay silent.
 * One scorer per family, so the record is exhaustive by type.
 */
const FAMILY_SCORERS: Record<SuggestionFamily, (ctx: ChatSuggestionContext) => number> = {
  issues: (ctx) => {
    if (above(ctx.overdueIssueCount, 0)) return 100;
    if (above(ctx.openIssueCount, 0)) return 80;
    return 30;
  },
  documents: (ctx) => {
    if (above(ctx.recentUploadCount, 0)) return 95;
    if (above(ctx.documentCount, 0)) return 90;
    return 40;
  },
  model: (ctx) => {
    if (ctx.ifcRevisionFilename !== null) return 95;
    if (above(ctx.ifcModelCount, 0)) return 70;
    return 0;
  },
  data: (ctx) => (ctx.canViewBuildingData ? 60 : 0),
  output: (ctx) => (above(ctx.documentCount, 0) || above(ctx.openIssueCount, 0) ? 50 : 0),
  guide: () => 10,
};

/**
 * Fill `{scope}`, `{count}` and `{filename}`.
 *
 * The replacement MUST be a function: with a string replacement, `$&`, `` $` ``,
 * `$'` and `$1` inside a building or file name are read as patterns and splice
 * the placeholder back into the sentence (#2309).
 */
function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(scope|count|filename)\}/g, (match, key: string) =>
    key in values ? values[key] : match
  );
}

function renderCandidate(
  candidate: SuggestionCandidate,
  copy: ExampleCopy,
  scopeName: string,
  ctx: ChatSuggestionContext
): string {
  const count = candidate.count?.(ctx) ?? null;
  return fill(copy[candidate.id], {
    scope: scopeName,
    filename: ctx.ifcRevisionFilename ?? "",
    ...(count === null ? {} : { count: String(count) }),
  });
}

/** How many questions the empty state shows. */
export const SUGGESTION_COUNT = 3;

/**
 * Pick the three questions for this scope. `seed` only rotates which wording a
 * family contributes — it never changes which families win, so the suggestions
 * stay a readout of the building's state rather than a shuffle.
 */
export function selectChatSuggestions(
  copy: ExampleCopy,
  scopeName: string,
  ctx: ChatSuggestionContext,
  seed: number
): string[] {
  const rotation = Number.isFinite(seed) ? Math.abs(Math.trunc(seed)) : 0;

  const ranked = FAMILY_ORDER.map((family) => ({
    family,
    score: FAMILY_SCORERS[family](ctx),
    eligible: CANDIDATES.filter((c) => c.family === family && c.isEligible(ctx)),
  }))
    .filter((entry) => entry.score > 0 && entry.eligible.length > 0)
    .sort(
      (a, b) => b.score - a.score || FAMILY_ORDER.indexOf(a.family) - FAMILY_ORDER.indexOf(b.family)
    )
    .slice(0, SUGGESTION_COUNT);

  return ranked.map((entry) =>
    renderCandidate(entry.eligible[rotation % entry.eligible.length], copy, scopeName, ctx)
  );
}
